/**
 * True only on a **published** Content Hub page URL: terminal
 * `/org/:organizationExternalId/page/:pageSlug` (optional trailing slash), with no
 * `/edit`, `/preview`, or `/create` segment after the slug.
 *
 * Used when playing a video may update the address bar (`replaceState`) with
 * `videoId` / `sectionId` — **not** on `/preview` or `/edit`, so those routes stay stable.
 *
 * Matches paths with an optional app prefix (e.g. `/canvas/org/.../page/...`).
 */
export function isPublishedContentHubVideoSharePath(
  pathname: string | undefined | null
) {
  if (!pathname) return false;
  const p = pathname.split("?")[0];

  return /\/org\/[^/]+\/page\/(?!create$)[^/]+\/?$/.test(p);
}

/**
 * True on a published hub page **or** the same hub under **preview**
 * (`…/page/:slug/preview`) or **edit** (`…/page/:slug/edit`, builder canvas).
 * Excludes `/page/create`.
 *
 * Use for in-player Share (copy link). Use {@link isPublishedContentHubVideoSharePath}
 * alone when the address bar must not change (e.g. syncing query params on play).
 */
export function isContentHubInPlayerSharePath(
  pathname: string | undefined | null
): boolean {
  if (!pathname) return false;
  const p = pathname.split("?")[0];
  if (/\/org\/[^/]+\/page\/create(\/|$)/.test(p)) {
    return false;
  }
  if (isPublishedContentHubVideoSharePath(p)) return true;

  const strippedPreview = p.replace(/\/preview\/?$/, "/");
  if (strippedPreview !== p) {
    if (/\/org\/[^/]+\/page\/create(\/|$)/.test(strippedPreview)) return false;
    return isPublishedContentHubVideoSharePath(strippedPreview);
  }

  const strippedEdit = p.replace(/\/edit\/?$/, "/");
  if (strippedEdit !== p) {
    if (/\/org\/[^/]+\/page\/create(\/|$)/.test(strippedEdit)) return false;
    return isPublishedContentHubVideoSharePath(strippedEdit);
  }

  return false;
}
